import React from 'react';
import '../../styles/animations.css';

// Basic Skeleton Components
export const SkeletonText = ({ width = '100%', height = '16px', className = '' }) => (
  <div 
    className={`skeleton ${className}`}
    style={{ width, height }}
  />
);

export const SkeletonTitle = ({ width = '60%', className = '' }) => (
  <div 
    className={`skeleton skeleton-title ${className}`}
    style={{ width }}
  />
);

export const SkeletonAvatar = ({ size = '48px', className = '' }) => (
  <div 
    className={`skeleton skeleton-avatar ${className}`}
    style={{ width: size, height: size }}
  />
);

// Card Skeleton for Lists
export const SkeletonCard = ({ children, className = '' }) => (
  <div className={`skeleton-card ${className}`}>
    {children}
  </div>
);

// Manager Tab Skeleton - User Card
export const SkeletonUserCard = () => (
  <SkeletonCard>
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
      <SkeletonAvatar />
      <div style={{ flex: 1 }}>
        <SkeletonTitle width="40%" />
        <SkeletonText width="60%" height="14px" />
      </div>
    </div>
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      <SkeletonText width="80px" height="24px" />
      <SkeletonText width="100px" height="24px" />
      <SkeletonText width="90px" height="24px" />
    </div>
  </SkeletonCard>
);

// Training Tab Skeleton - Quiz Card
export const SkeletonQuizCard = () => (
  <SkeletonCard>
    <div style={{ marginBottom: '12px' }}>
      <SkeletonTitle width="50%" />
      <SkeletonText width="80%" height="14px" />
    </div>
    <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
      <SkeletonText width="100px" height="20px" />
      <SkeletonText width="120px" height="20px" />
    </div>
  </SkeletonCard>
);

// Table Row Skeleton
export const SkeletonTableRow = () => (
  <div style={{ 
    display: 'grid', 
    gridTemplateColumns: 'repeat(4, 1fr)', 
    gap: '16px',
    padding: '16px',
    borderBottom: '1px solid var(--border)'
  }}>
    <SkeletonText width="80%" />
    <SkeletonText width="60%" />
    <SkeletonText width="70%" />
    <SkeletonText width="50%" />
  </div>
);

// Summary Card Skeleton (for dashboards)
export const SkeletonSummaryCard = () => (
  <div style={{
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px'
  }}>
    <SkeletonText width="40%" height="14px" style={{ marginBottom: '12px' }} />
    <SkeletonTitle width="60%" />
    <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
      <SkeletonText width="80px" height="32px" />
      <SkeletonText width="100px" height="32px" />
    </div>
  </div>
);

// List Item Skeleton
export const SkeletonListItem = () => (
  <div style={{
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }}>
    <SkeletonAvatar size="40px" />
    <div style={{ flex: 1 }}>
      <SkeletonText width="70%" height="16px" style={{ marginBottom: '8px' }} />
      <SkeletonText width="50%" height="12px" />
    </div>
    <SkeletonText width="80px" height="32px" />
  </div>
);

// Compact list skeleton — 3 items (fallback dla małych paneli zarządzania)
export const SkeletonListFallback = ({ count = 3 }) => (
  <div data-testid="skeleton-list-fallback" style={{ padding: '10px 0' }}>
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: '10px',
          padding: '14px 16px',
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
        }}
      >
        <SkeletonAvatar size="36px" />
        <div style={{ flex: 1 }}>
          <SkeletonText width="55%" height="14px" />
          <div style={{ height: 4 }} />
          <SkeletonText width="75%" height="11px" />
        </div>
        <SkeletonText width="70px" height="28px" />
      </div>
    ))}
  </div>
);

// Loading Dots Component
export const LoadingDots = ({ color = 'var(--gold)' }) => (
  <div className="loading-dots">
    <span style={{ background: color }} />
    <span style={{ background: color }} />
    <span style={{ background: color }} />
  </div>
);

// Enhanced Spinner
export const Spinner = ({ size = '40px', className = '' }) => (
  <div 
    className={`spinner-enhanced ${className}`}
    style={{ width: size, height: size }}
  />
);

// Loading State with Message
export const LoadingState = ({ message = 'Ładowanie...', showSpinner = true }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '20px'
  }}>
    {showSpinner && <Spinner />}
    <div style={{
      fontSize: '16px',
      color: 'var(--text-muted)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}>
      {message}
      <LoadingDots />
    </div>
  </div>
);

// Success Checkmark SVG Component
export const SuccessCheckmark = ({ size = '64px' }) => (
  <svg 
    className="success-checkmark"
    width={size} 
    height={size} 
    viewBox="0 0 64 64"
    style={{ display: 'block' }}
  >
    <circle 
      cx="32" 
      cy="32" 
      r="30" 
      fill="none" 
      stroke="var(--gold)" 
      strokeWidth="3"
    />
    <path 
      d="M20 32 L28 40 L44 24" 
      fill="none" 
      stroke="var(--gold)" 
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default {
  SkeletonText,
  SkeletonTitle,
  SkeletonAvatar,
  SkeletonCard,
  SkeletonUserCard,
  SkeletonQuizCard,
  SkeletonTableRow,
  SkeletonSummaryCard,
  SkeletonListItem,
  LoadingDots,
  Spinner,
  LoadingState,
  SuccessCheckmark
};
